import { useEffect, useState } from "react";
import { Reveal, SectionHeading } from "./Section";
import { Compass, Users, Globe2, Lightbulb } from "lucide-react";
import { api, imageUrl } from "@/lib/api";

const PILLARS = [
  { icon: Lightbulb, title: "Technology", text: "Software engineering roots, AI, IoT and digital infrastructure." },
  { icon: Compass, title: "Entrepreneurship", text: "From a college venture to companies across aviation, healthcare and agriculture." },
  { icon: Globe2, title: "Capital & Markets", text: "Fundraising, search fund and cross-border business between India and the world." },
  { icon: Users, title: "People", text: "Bringing together the right people, ideas and markets to create long-term value." },
];

const About = () => {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  const paragraphs = profile?.biography
    ? profile.biography.split("\n").filter(Boolean)
    : [
        "I am an entrepreneur, technology builder and business strategist focused on building businesses, connecting markets and creating strategic partnerships across India and international markets.",
        "My journey began during college with my first entrepreneurial venture and has evolved across technology, aviation, mining, fintech, agriculture, healthcare and international business development.",
        "I work at the intersection of technology, entrepreneurship, capital, people and opportunity — bringing together the right people, ideas and markets to build businesses that can create long-term value.",
      ];

  return (
    <section id="about" className="relative py-28 md:py-36" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading index="01" eyebrow="About Me" title={<>Building with purpose.<br /><span className="text-slate-500">Connecting people, ideas and markets.</span></>} />
        <div className="grid gap-14 lg:grid-cols-[1.15fr_1fr]">
          <Reveal>
            <div>
              {profile?.portrait_url && (
                <div className="relative mb-12 w-fit" data-testid="about-portrait">
                  <div className="absolute -inset-3 rounded-3xl border border-[#E2C08D]/25 rotate-2" />
                  <img src={imageUrl(profile.portrait_url)} alt="Bishwa Mohan Pati" className="relative h-72 w-60 rounded-2xl object-cover border border-white/10 shadow-[0_24px_70px_rgba(0,0,0,0.55)]" />
                  <div className="absolute -bottom-4 left-5 rounded-full border border-[#E2C08D]/40 bg-[#0A0C10] px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E2C08D]">Founder & Builder</div>
                </div>
              )}
              <div className="space-y-6 text-base md:text-lg text-slate-300 leading-relaxed">
                {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <blockquote className="mt-10 border-l-2 border-[#E2C08D] pl-6" data-testid="about-belief-quote">
                <p className="font-display italic text-2xl md:text-3xl text-slate-200 leading-snug">“A business becomes meaningful when what we build creates value beyond ourselves.”</p>
              </blockquote>
            </div>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="group h-full rounded-2xl border border-white/8 bg-[#10141d] p-6 hover:border-[#E2C08D]/30 hover:bg-[#141926] transition-colors duration-500" data-testid={`about-pillar-${p.title.toLowerCase().replace(/\s|&/g, "-")}`}>
                  <p.icon size={22} className="text-[#E2C08D] mb-4" />
                  <h3 className="font-display text-xl font-semibold text-slate-100 mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
